import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Modal } from "../../../ui/components/Modal";
import { Text } from "../../../ui/components/Text";
import { ListView } from "../../../ui/components/ListView";
import type { HistoryEntityLink } from "../../../ui/components/ListView/logSummaryLinks";
import type {
  CursorPage,
  CursorPageArgs,
} from "../../../ui/components/Table/hooks/useInfiniteListQuery";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import {
  fetchUnifiedHistoryFeed,
  type HistoryEvent,
} from "../../lib/api/history";
import { createHistoryTableColumns } from "../system/historyTableColumns";

export type SubscriptionNotificationHistoryModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  productId: string;
  productName: string;
  userId: string;
  userLabel: string;
};

export function SubscriptionNotificationHistoryModal({
  open,
  setOpen,
  productId,
  productName,
  userId,
  userLabel,
}: SubscriptionNotificationHistoryModalProps) {
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const { t } = useI18n();
  const token = accessToken ?? localStorage.getItem("access_token");

  const columns = React.useMemo(() => createHistoryTableColumns(t), [t]);

  const lockedFilters = React.useMemo(
    () => ({
      userIds: [userId],
      productIds: [productId],
    }),
    [userId, productId],
  );

  const fetchPage = React.useCallback(
    async (args: CursorPageArgs): Promise<CursorPage<HistoryEvent>> => {
      if (!token) return { data: [], nextCursor: null, hasMore: false };
      const page = await fetchUnifiedHistoryFeed(token, {
        limit: args.limit,
        cursor: args.cursor,
        search: args.search,
        order: args.order,
        filters: {
          ...args.filters,
          ...lockedFilters,
        },
      });
      return {
        data: page.data,
        nextCursor: page.nextCursor,
        hasMore: page.hasMore,
      };
    },
    [token, lockedFilters],
  );

  const onLogEntityClick = React.useCallback(
    (link: HistoryEntityLink) => {
      setOpen(false);
      if (link.kind === "user") {
        void navigate({
          to: "/workspace/users/$userId",
          params: { userId: link.id },
        });
        return;
      }
      if (link.kind === "organization") {
        void navigate({
          to: "/workspace/organizations/$orgId",
          params: { orgId: link.id },
        });
        return;
      }
      if (link.kind === "product") {
        void navigate({
          to: "/workspace/products/$productId",
          params: { productId: link.id },
        });
      }
    },
    [navigate, setOpen],
  );

  const cursorFeed = React.useMemo(() => ({ fetchPage }), [fetchPage]);

  if (!open) return null;

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      minWidth="720px"
      maxWidth="960px"
      maxHeight="85vh"
    >
      <Modal.Header>
        <Text level="h3" weight="bold">
          {t("pro.products.subscriptions.history.title")}
        </Text>
        <Text
          level="body-small"
          style={{
            color: "var(--color-fgMuted, #9CA3AF)",
            marginTop: 4,
            display: "block",
          }}
        >
          {t("pro.products.subscriptions.history.subtitle", {
            user: userLabel,
            product: productName,
          })}
        </Text>
      </Modal.Header>
      <Modal.Body>
        <ListView<HistoryEvent>
          listId={`subscription-history:${productId}:${userId}`}
          userId={user?.id ?? "anonymous"}
          columns={columns}
          fetchList={async () => ({ data: [], total: 0 })}
          cursorFeed={cursorFeed}
          contextKey={`${productId}:${userId}`}
          enabledViews={["log"]}
          defaultSort={{ sort_by: "occurredAt", order: "desc" }}
          defaultPageSize={40}
          searchPlaceholder={t("pro.system.history.search")}
          emptyTitle={t("pro.products.subscriptions.history.emptyTitle")}
          emptySubtitle={t("pro.products.subscriptions.history.emptySubtitle")}
          timeRangeFilter={{ enabled: true }}
          onLogEntityClick={onLogEntityClick}
          disableLocalPersistence
        />
      </Modal.Body>
    </Modal>
  );
}
